import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

export default function PartyInfoPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState<{
    name?: string; member_type?: string; member_id?: string; avatar?: string;
    created_at?: string; kyc_date?: string; region?: string;
    kyc_invites_count?: number; study_days?: number; consecutive_study_days?: number;
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get('/me').then(({ data }) => setUser(data)).catch(() => navigate('/login'));
  }, [navigate]);

  const resolveImg = (u?: string) => {
    if (!u) return undefined;
    if (/^https?:\/\//.test(u)) return u;
    if (u.startsWith('/')) {
      try { return `${new URL(api.defaults.baseURL ?? '').origin}${u}`; } catch { return u; }
    }
    return u;
  };

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || uploading) return;
    const formData = new FormData();
    formData.append('avatar', file);
    setUploading(true);
    try {
      const { data } = await api.post('/me/avatar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUser((prev) => (prev ? { ...prev, avatar: data.avatar } : prev));
    } catch (err: any) {
      alert(err?.response?.data?.message ?? '头像上传失败，请重试');
    } finally {
      setUploading(false);
    }
  };

  const memberTypeLabel = user?.member_type === 'formal' ? '正式党员' : user?.member_type === 'intern' ? '实习党员' : '预备党员';
  const registrationDate = (user?.kyc_date ?? user?.created_at) ? new Date(user?.kyc_date ?? user?.created_at!).toLocaleDateString('zh-CN') : '';

  const invites = user?.kyc_invites_count ?? 0;
  const studyDays = user?.study_days ?? 0;
  const consecutiveStudy = user?.consecutive_study_days ?? 0;
  const isIntern = ['intern', 'formal'].includes(user?.member_type ?? '');
  const isFormal = user?.member_type === 'formal';

  const tiers = [
    {
      icon: '/images/xunxi_yubei.png',
      label: '预备党员权益',
      items: [
        '*申请党员转向补贴补助金105万',
        '*享受每日党政日想学习获取现金红包支票等权益',
      ],
      condition: '获取条件：使命注册并成功入党',
      progress: '',
      achieved: true,
    },
    {
      icon: '/images/xunxi_shixi.png',
      label: '实习党员权益',
      items: [
        '*申请党员转向补贴补助金105万',
        '*享受每日党政日想学习获取现金红包支票等权益',
        '*免费进行大额体现补助资金及加速到账',
      ],
      condition: '获取条件：邀请3位好友成为预备党员或连续签到学习5日',
      progress: isIntern ? '' : `当前进度：已邀请${invites}/3人，连续学习${consecutiveStudy}/5天`,
      achieved: isIntern,
    },
    {
      icon: '/images/xunxi_zhengshi.png',
      label: '正式党员权益',
      items: [
        '*申请党员转向补贴补助金105万',
        '*享受每日党政日想学习获取现金红包支票等权益',
        '*免费获得一次性进行大额提现补助资金到账及支票兑换权限',
        '*获得党员之家荣誉及党员家庭补助资金300万元',
      ],
      condition: '获取条件：实习党员完成党员思想学习15天',
      progress: isFormal ? '' : isIntern ? `当前进度：已学习${studyDays}/15天` : '',
      achieved: isFormal,
    },
  ];

  return (
    <div className="max-w-md mx-auto min-h-screen" style={{ backgroundColor: '#8b0000' }}>
      <AuthGuard />

      {/* Header */}
      <div className="relative">
        <img src="/images/xunxi_shang.png" alt="" className="w-full" />
      </div>

      {/* Member info card */}
      <div className="px-4 mx-2 -mt-[228px] mb-[20px] relative z-10">
        <div className="flex items-center gap-2 mb-1">
          <img src="/images/xunxi_dnayuanxinxi.png" alt="" className="w-[120px]" />
        </div>

        <div className="mx-4">
          <h2 className="text-lg text-[#c4161c] mb-1">您好，{user?.name ?? ''}同志</h2>

          <div className="flex gap-4">
            {/* Avatar upload */}
            <label className="relative w-20 h-24 bg-gray-300 rounded-lg flex-shrink-0 overflow-hidden cursor-pointer block">
              {user?.avatar ? (
                <img src={resolveImg(user.avatar)} alt="头像" className="w-full h-full object-cover" />
              ) : (
                <img src={"/images/avatar.png"} alt="头像" className="w-full h-full object-cover" />
              )}
              <div className="absolute bottom-0 inset-x-0 bg-black/50 text-white text-[10px] text-center py-0.5">
                {uploading ? '上传中…' : '上传头像'}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="absolute w-0 h-0 opacity-0 overflow-hidden"
                onChange={handleAvatarChange}
              />
            </label>

            {/* Info fields */}
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-600 w-16 flex-shrink-0">登记日期：</span>
                <div className="flex-1 bg-[#ffe8d0] rounded px-2 py-1 text-[11px] text-gray-800">{registrationDate}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-600 w-16 flex-shrink-0">党员编号：</span>
                <div className="flex-1 bg-[#ffe8d0] rounded px-2 py-1 text-[11px] text-gray-800">{user?.member_id ?? '-'}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-600 w-16 flex-shrink-0">党员性质：</span>
                <div className="flex-1 bg-[#ffe8d0] rounded px-2 py-1 text-[11px] text-gray-800">{memberTypeLabel}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-gray-600 w-16 flex-shrink-0">所在地区：</span>
                <div className="flex-1 bg-[#ffe8d0] rounded px-2 py-1 text-[11px] text-gray-800">{user?.region ?? '-'}</div>
              </div>
            </div>
          </div>

          {/* Report to party */}
          <div className="mt-2 text-center">
            <span className="bg-[#ac0000] text-[#fff9cc] font-bold text-xs py-1 px-4 rounded-2xl">★ 我在 {user?.region ?? 'xx'} 向党报告，祝党生日快乐 ★</span>
          </div>

        </div>
      </div>

      <div className="relative">
        <div className="bg-[url(/images/xunxi_xiabg.png)] w-full bg-no-repeat bg-center bg-[size:100%_100%]">
          {/* Member rights section */}
          <div className="mx-[28px] mt-4 border border-[#e3a067] rounded-2xl">
            <div className="text-center mb-3 -mt-[15px]">
              <img src={"/images/xunxi_quanyi.png"} alt="xunxi_quanyi" className="w-[200px] mx-auto" />
            </div>

            <div className="space-y-3 mb-2">
              {tiers.map((tier, index) => (
                <div key={tier.label} className="px-4">
                  <div className="flex flex-row gap-3">
                    <img src={tier.icon} alt="" className="w-8 h-8" />
                    <div className="flex flex-col">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[#c4161c] font-bold text-xs">{tier.label}</span>
                        </div>
                        {tier.achieved && (
                          <div className="flex items-center gap-1">
                            <span className="text-green-600 text-xs">✓</span>
                            <span className="text-green-600 text-xs font-bold">已达标</span>
                          </div>
                        )}
                      </div>
                      <div className="space-y-[2px] mb-1">
                        {tier.items.map((item, i) => (
                          <div key={i} className="text-[10px] text-gray-700">{item}</div>
                        ))}
                      </div>
                      <p className="text-[10px] text-[#c4161c]">{tier.condition}</p>
                      {tier.progress && <p className="text-[10px] text-[#e6a817] font-bold mt-0.5">{tier.progress}</p>}
                      {index !== tiers.length - 1 ? <img src={"/images/xunxi_xixian.png"} alt="xunxi_xixian" className="w-full mt-1" /> : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <img src={"/images/xunxi_xia.png"} className="w-full" />
        </div>
      </div>
    </div>
  );
}
